import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import "./Orders.css"; // Importă fișierul CSS
import Papa from "papaparse";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css"; // Stiluri pentru date picker
import { format } from "date-fns"; // Pentru a formata data

const OrderSearch = () => {
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null); // Data selectată pentru filtrare

  const fetchAllOrders = async () => {
    setLoading(true);
    setError(null);

    try {
      const ordersRef = collection(db, "Orders");
      const querySnapshot = await getDocs(ordersRef);

      if (querySnapshot.empty) {
        console.log("Nu sunt comenzi.");
      } else {
        const allOrders = [];
        querySnapshot.forEach((doc) => {
          allOrders.push({ id: doc.id, ...doc.data() });
        });

        // Sortăm comenzile după dată, verificând și convertind șirurile de caractere
        allOrders.sort((a, b) => {
          const dateA = a.orderDate ? new Date(a.orderDate) : new Date(0);
          const dateB = b.orderDate ? new Date(b.orderDate) : new Date(0);

          return dateB - dateA;
        });

        setOrders(allOrders);
      }
    } catch (err) {
      console.error("Eroare la încărcarea comenzilor:", err);
      setError("A apărut o eroare. Te rugăm să încerci din nou.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllOrders(); // Încărcăm toate comenzile la montarea componentei
  }, []);

  const handleUpdateStatus = async (id) => {
    const newStatus = prompt("Introduceți noul status al comenzii:");

    if (newStatus) {
      try {
        // Referința la documentul comenzii
        const orderRef = doc(db, "Orders", id);

        // Actualizează statusul comenzii
        await updateDoc(orderRef, {
          status: newStatus,
        });

        // Actualizează lista de comenzi pentru a reflecta schimbările
        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order.id === id ? { ...order, status: newStatus } : order
          )
        );

        alert("Statusul comenzii a fost actualizat cu succes!");
      } catch (err) {
        console.error("Eroare la actualizarea statusului:", err);
        alert("A apărut o eroare la actualizarea statusului.");
      }
    } else {
      alert(
        "Statusul nu a fost actualizat. Te rog să introduci un status valid."
      );
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    setError(null);

    try {
      const ordersRef = collection(db, "Orders");

      // Obține luna și anul în format MM-yyyy
      const monthYear = selectedDate ? format(selectedDate, "MM-yyyy") : null;

      if (selectedDate && isNaN(selectedDate.getTime())) {
        throw new Error("Data selectată este invalidă");
      }

      let combinedOrders = [];

      // Căutare după dată
      if (monthYear) {
        const [month, year] = monthYear.split("-");
        const firstDayOfMonth = new Date(`${year}-${month}-01T00:00:00`);
        console.log("First day of month:", firstDayOfMonth);

        // Verifică dacă data este validă
        if (isNaN(firstDayOfMonth.getTime())) {
          throw new Error("Data construită nu este validă");
        }

        // Creează un timestamp pentru începutul lunii
        const startTimestamp = Timestamp.fromDate(firstDayOfMonth);

        // Construiește data pentru sfârșitul lunii (ultima zi a lunii)
        const lastDayOfMonth = new Date(firstDayOfMonth);
        lastDayOfMonth.setMonth(lastDayOfMonth.getMonth() + 1);
        lastDayOfMonth.setDate(0); // Ultima zi a lunii curente
        lastDayOfMonth.setHours(23);
        lastDayOfMonth.setMinutes(59);
        lastDayOfMonth.setSeconds(59);
        lastDayOfMonth.setMilliseconds(999);
        console.log("Last day of month:", lastDayOfMonth);

        const endTimestamp = Timestamp.fromDate(lastDayOfMonth);

        // Filtrare după dată
        const qDate = query(
          ordersRef,
          where("orderDate", ">=", startTimestamp),
          where("orderDate", "<=", endTimestamp)
        );

        const querySnapshotDate = await getDocs(qDate);
        querySnapshotDate.forEach((doc) => {
          const orderData = doc.data();
          const orderDate = orderData.orderDate
            ? orderData.orderDate instanceof Timestamp
              ? orderData.orderDate.toDate()
              : new Date(orderData.orderDate)
            : null;

          // Log pentru a vedea data din Firestore
          console.log("Order date from Firestore:", orderDate);

          // Verifică dacă data este în intervalul dorit
          if (
            orderDate >= startTimestamp.toDate() &&
            orderDate <= endTimestamp.toDate()
          ) {
            combinedOrders.push({ id: doc.id, ...orderData, orderDate });
          }
        });
      }

      // Căutare după nume și prenume
      if (name && surname) {
        const qNameSurname = query(
          ordersRef,
          where("customerData.name", "==", name),
          where("customerData.surname", "==", surname)
        );

        const querySnapshotNameSurname = await getDocs(qNameSurname);
        querySnapshotNameSurname.forEach((doc) => {
          combinedOrders.push({ id: doc.id, ...doc.data() });
        });
      }

      // Returnează toate comenzile dacă nu există niciuna dintre condițiile de căutare
      if (!monthYear && !name && !surname) {
        const querySnapshot = await getDocs(ordersRef);
        querySnapshot.forEach((doc) => {
          combinedOrders.push({ id: doc.id, ...doc.data() });
        });
      }

      // Elimină comenzile duplicate
      const uniqueOrders = Array.from(
        new Set(combinedOrders.map((a) => a.id))
      ).map((id) => combinedOrders.find((a) => a.id === id));

      setOrders(uniqueOrders);
    } catch (err) {
      console.error("Eroare la căutare:", err);
      setError("A apărut o eroare. Te rugăm să încerci din nou.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Sigur dorești să ștergi această comandă?")) {
      try {
        await deleteDoc(doc(db, "Orders", id));
        setOrders(orders.filter((order) => order.id !== id));
        alert("Comanda a fost ștearsă cu succes!");
      } catch (err) {
        console.error("Eroare la ștergere:", err);
        alert("A apărut o eroare la ștergerea comenzii.");
      }
    }
  };

  const handleExportCSV = () => {
    if (orders.length === 0) {
      alert("Nu există comenzi de exportat!");
      return;
    }

    const csvData = orders.map((order) => ({
      Nume: order.customerData.name,
      Prenume: order.customerData.surname,
      Status: order.status,
      "Tip Client": order.customerType,
      "Info PJ": order.businessDetails
        ? `"${order.businessDetails.businessName}, Reg. Com: ${order.businessDetails.registrationNumber}, CUI: ${order.businessDetails.cui}, Adresă: ${order.businessDetails.fullAddress}"`
        : "N/A",
      "Metoda Livrare": order.delivery,
      Email: order.contact.email,
      Telefon: order.contact.phone,
      Adresă: `${order.address.street}, ${order.address.number}, ${order.address.city}, ${order.address.county} - ${order.address.postCode}`,
      Produse: order.items
        .map((item) => `${item.name} (x${item.quantity})`)
        .join(", "),
      Total: `${order.totalPrice} RON`,
      "Data Comandă": order.orderDate,
      Mențiuni: order.mentiuni || "N/A",
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const today = new Date();
    const formattedDate = `${today.getFullYear()}_${String(
      today.getMonth() + 1
    ).padStart(2, "0")}_${String(today.getDate()).padStart(2, "0")}`;
    const filename = `${formattedDate}_comenzi_firu_artei.csv`;

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="order-search-container">
      <h2>Căutare Comandă</h2>
      <div className="search-inputs">
        <input
          className="checkout-input"
          type="text"
          placeholder="Nume"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          className="checkout-input"
          type="text"
          placeholder="Prenume"
          value={surname}
          onChange={(e) => setSurname(e.target.value)}
        />
        <DatePicker
          selected={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          dateFormat="MM/yyyy"
          showMonthYearPicker
          placeholderText="Selectează lună și an"
        />

        <button
          onClick={handleSearch}
          disabled={loading || (!name && !surname && !selectedDate)}
          className="search-button"
        >
          {loading ? "Căutare..." : "Caută"}
        </button>

        <button
          onClick={fetchAllOrders}
          disabled={loading}
          className="fetch-all-orders-button"
        >
          {loading ? "Încărcare..." : "Încarcă Toate Comenzile"}
        </button>

        <button onClick={handleExportCSV} className="fetch-all-orders-button">
          Exportă CSV
        </button>
      </div>

      {error && <p className="error-message">{error}</p>}

      {orders.length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Nume</th>
                <th>Prenume</th>
                <th>Status</th>
                <th>Tip Client</th>
                <th>Info PJ</th>
                <th>Metoda Livrare</th>
                <th>Email</th>
                <th>Telefon</th>
                <th>Adresă</th>
                <th>Produse</th>
                <th>Total</th>
                <th>Data</th>
                <th>Mentiuni</th>
                <th>Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td>{order.id}</td>
                  <td>{order.customerData.name}</td>
                  <td>{order.customerData.surname}</td>
                  <td>{order.status}</td>
                  <td>{order.customerType}</td>
                  <td
                    dangerouslySetInnerHTML={{
                      __html: order.businessDetails
                        ? `Nume firma: ${order.businessDetails.businessName} 
        <br />Nr. reg. com.: ${order.businessDetails.registrationNumber}
        <br />CUI/CIF: ${order.businessDetails.cui}
        <br />Adresa: ${order.businessDetails.fullAddress}`
                        : "N/A",
                    }}
                  ></td>
                  <td>{order.delivery}</td>
                  <td>{order.contact.email}</td>
                  <td>{order.contact.phone}</td>
                  <td>
                    {order.address.street}, {order.address.number},{" "}
                    {order.address.city}, {order.address.county} -{" "}
                    {order.address.postCode}
                  </td>
                  <td>
                    {order.items.map((item, index) => (
                      <div key={index}>
                        {item.name} - Cantitate: {item.quantity}
                        <br></br>
                      </div>
                    ))}
                  </td>
                  <td>{order.totalPrice} RON</td>
                  <td>
                    {order.orderDate
                      ? format(
                          order.orderDate.toDate
                            ? order.orderDate.toDate()
                            : order.orderDate,
                          "dd/MM/yyyy"
                        )
                      : "N/A"}
                  </td>

                  <td>{order.mentiuni}</td>
                  <td
                    style={{
                      padding: "10px",
                      borderBottom: "1px solid #ddd",
                      textAlign: "center",
                    }}
                  >
                    <div className="actions-container">
                      <button
                        onClick={() => handleDelete(order.id)}
                        className="delete-order-button"
                      >
                        Șterge
                      </button>
                      <button
                        onClick={() => handleUpdateStatus(order.id)}
                        className="update-status-button"
                      >
                        Actualizează Status
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OrderSearch;
